import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { customerAPI } from '../../api/index';
import { queryClient } from '../../App';
import { validateAadhaar, validateEmail, validatePAN, validatePhone } from '../../lib/validation';
import { Customer } from '../../types/index';
import { Button } from '../Button';

interface AddCustomerModalProps {
  open: boolean
  onClose: () => void
  customer?: Customer
  onSuccess?: () => void
}

export function AddCustomerModal({
  open,
  onClose,
  customer,
  onSuccess,
}: AddCustomerModalProps) {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    alt_phone: '',
    email: '',
    dob: '',
    gender: 'other' as 'male' | 'female' | 'other',
    address: '',
    city: '',
    state: '',
    pincode: '',
    aadhaar_number: '',
    pan_number: '',
    notes: '',
  })

  useEffect(() => {
    if (customer) {
      setFormData({
        full_name: customer.full_name || '',
        phone: customer.phone || '',
        alt_phone: customer.alt_phone || '',
        email: customer.email || '',
        dob: customer.dob || '',
        gender: customer.gender || 'other',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        pincode: customer.pincode || '',
        aadhaar_number: customer.aadhaar_number || '',
        pan_number: customer.pan_number || '',
        notes: customer.notes || '',
      })
    } else {
      setFormData({
        full_name: '',
        phone: '',
        alt_phone: '',
        email: '',
        dob: '',
        gender: 'other',
        address: '',
        city: '',
        state: '',
        pincode: '',
        aadhaar_number: '',
        pan_number: '',
        notes: '',
      })
    }
  }, [customer, open])

  const createMutation = useMutation({
    mutationFn: () => customerAPI.create(formData),
    onSuccess: () => {
      toast.success('Customer created')
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create customer')
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => customerAPI.update(customer!.id, formData),
    onSuccess: () => {
      toast.success('Customer updated')
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customer', customer!.id] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update customer')
    },
  })

  const isLoading = createMutation.isPending || updateMutation.isPending
  const isEdit = !!customer

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.full_name.trim() || !formData.phone.trim()) {
      toast.error('Name and phone are required')
      return
    }

    // Validate phone number
    if (!validatePhone(formData.phone)) {
      toast.error('Please enter a valid phone number (10 digits)')
      return
    }

    // Validate alternate phone if provided
    if (formData.alt_phone && !validatePhone(formData.alt_phone)) {
      toast.error('Please enter a valid alternate phone number (10 digits)')
      return
    }

    // Validate email if provided
    if (formData.email && !validateEmail(formData.email)) {
      toast.error('Please enter a valid email address')
      return
    }

    // Validate PAN if provided
    if (formData.pan_number && !validatePAN(formData.pan_number)) {
      toast.error('Please enter a valid PAN number (e.g., ABCDE1234F)')
      return
    }

    // Validate Aadhaar if provided
    if (formData.aadhaar_number && !validateAadhaar(formData.aadhaar_number)) {
      toast.error('Please enter a valid Aadhaar number (12 digits)')
      return
    }

    // Convert empty strings to null for optional fields
    const cleanedData = {
      full_name: formData.full_name,
      phone: formData.phone,
      alt_phone: formData.alt_phone?.trim() || null,
      email: formData.email?.trim() || null,
      dob: formData.dob?.trim() || null,
      gender: formData.gender || null,
      address: formData.address?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      pincode: formData.pincode?.trim() || null,
      aadhaar_number: formData.aadhaar_number?.trim() || null,
      pan_number: formData.pan_number?.trim() || null,
      notes: formData.notes?.trim() || null,
    }

    if (isEdit) {
      customerAPI.update(customer!.id, cleanedData).then(() => {
        toast.success('Customer updated')
        queryClient.invalidateQueries({ queryKey: ['customers'] })
        queryClient.invalidateQueries({ queryKey: ['customer', customer!.id] })
        onClose()
        onSuccess?.()
      }).catch((error) => {
        toast.error(error.response?.data?.error ?? 'Failed to update customer')
      })
    } else {
      customerAPI.create(cleanedData).then(() => {
        toast.success('Customer created')
        queryClient.invalidateQueries({ queryKey: ['customers'] })
        onClose()
        onSuccess?.()
      }).catch((error) => {
        toast.error(error.response?.data?.error ?? 'Failed to create customer')
      })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Customer' : 'Add Customer'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Mobile No *
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="+91 98765 43210 or 9876543210"
              />
              {formData.phone && !validatePhone(formData.phone) && (
                <p className="mt-1 text-xs text-red-600">Invalid phone number. Use 10 digits.</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Alternate Mobile No
              </label>
              <input
                type="tel"
                value={formData.alt_phone}
                onChange={(e) =>
                  setFormData({ ...formData, alt_phone: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="+91 98765 43211 or 9876543211"
              />
              {formData.alt_phone && !validatePhone(formData.alt_phone) && (
                <p className="mt-1 text-xs text-red-600">Invalid phone number. Use 10 digits.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="john@example.com"
              />
              {formData.email && !validateEmail(formData.email) && (
                <p className="mt-1 text-xs text-red-600">Invalid email address.</p>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.dob}
                onChange={(e) =>
                  setFormData({ ...formData, dob: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    gender: e.target.value as 'male' | 'female' | 'other',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Pincode
              </label>
              <input
                type="text"
                value={formData.pincode}
                onChange={(e) =>
                  setFormData({ ...formData, pincode: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="400001"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="Mumbai"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                State
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="Maharashtra"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Aadhaar No.
              </label>
              <input
                type="text"
                value={formData.aadhaar_number}
                onChange={(e) =>
                  setFormData({ ...formData, aadhaar_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="1234 5678 9012"
              />
              {formData.aadhaar_number && !validateAadhaar(formData.aadhaar_number) && (
                <p className="mt-1 text-xs text-red-600">Invalid Aadhaar. Use 12 digits.</p>
              )}
            </div>
          </div>

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                PAN No.
              </label>
              <input
                type="text"
                value={formData.pan_number}
                onChange={(e) =>
                  setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
                placeholder="ABCDE1234F"
              />
              {formData.pan_number && !validatePAN(formData.pan_number) && (
                <p className="mt-1 text-xs text-red-600">Invalid PAN. Format: AAAAA9999A</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
              placeholder="Enter full address"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-saffron focus:outline-none"
              placeholder="Add any additional notes"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddCustomerModal
